import { useEffect, useMemo, useState } from "react";
import TableContainer from "@mui/material/TableContainer";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import { SortableTableHead } from "../../common/SortableTableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import { usePatrolsStore } from "./patrolsStore/patrolsStore";
import { formatPatrolTime } from "./patrolsUtils/formatPatrolTimestamp";
import { useNavigate } from "react-router-dom";
import { Order } from "../Alerts/AlertsUtils/alertsInterfaces";
import {
  getComparator,
  stableSort,
} from "../Alerts/AlertsUtils/alertsTableFunctions";

const CHM_HEAD_CELLS = [
  { id: "camera_name", label: "Camera Name" },
  { id: "timestamp", label: "Time" },
  { id: "description", label: "Issue Type" },
] as const;

export const CHMWarningsTableSelectedPatrol = () => {
  const navigate = useNavigate();
  const {
    patrolCHMIssues,
    selectedPatrolCHMIndex,
    setSelectedPatrolCHMIndex,
    fetchPatrolCHMImage,
  } = usePatrolsStore();

  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>("timestamp");

  const sortedIssues = useMemo(
    () => stableSort(patrolCHMIssues, getComparator(order, orderBy)),
    [patrolCHMIssues, order, orderBy]
  );

  const handleRequestSort = (
    _event: React.MouseEvent<unknown>,
    property: string
  ) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  // Auto-select first row and fetch image when CHM data changes (use first row of sorted list)
  useEffect(() => {
    const sorted = stableSort(
      patrolCHMIssues,
      getComparator(order, orderBy)
    );
    if (sorted.length > 0) {
      setSelectedPatrolCHMIndex(0);
      const imageKey = sorted[0]?.image_s3_key;
      if (imageKey != null) {
        fetchPatrolCHMImage(
          typeof imageKey === "string" ? imageKey : String(imageKey),
          navigate
        );
      }
    }
  }, [
    patrolCHMIssues,
    setSelectedPatrolCHMIndex,
    fetchPatrolCHMImage,
    navigate,
  ]);

  const handleRowClick = (index: number) => {
    setSelectedPatrolCHMIndex(index);
    const imageKey = sortedIssues[index]?.image_s3_key;
    if (imageKey != null) {
      fetchPatrolCHMImage(
        typeof imageKey === "string" ? imageKey : String(imageKey),
        navigate
      );
    }
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        height: 450,
        width: "100%",
        maxWidth: 800,
        minWidth: 0,
        backgroundColor: "#f8fafd",
        overflow: "auto",
      }}
    >
      <Table stickyHeader>
        <SortableTableHead
          headCells={[...CHM_HEAD_CELLS]}
          order={order}
          orderBy={orderBy}
          onRequestSort={handleRequestSort}
          backgroundColor="#e3ecfa"
        />
        <TableBody>
          {sortedIssues.length > 0 ? (
            sortedIssues.map((issue, index) => (
              <TableRow
                key={`${issue.device_id}-${index}`}
                onClick={() => handleRowClick(index)}
                sx={{
                  backgroundColor:
                    selectedPatrolCHMIndex === index ? "#d4e4f7" : "inherit",
                  "&:hover": {
                    backgroundColor:
                      selectedPatrolCHMIndex === index ? "#c4d8f0" : "#eaf1fb",
                    cursor: "pointer",
                  },
                }}
              >
                <TableCell sx={{ width: "35%" }}>
                  {issue.site_id && issue.camera_id && issue.camera_name ? (
                    <a
                      href={`sites/${issue.site_id}/editcamera/${issue.camera_id}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        color: "#0d47a1",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#002171";
                        e.currentTarget.style.textDecoration = "underline";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#0d47a1";
                        e.currentTarget.style.textDecoration = "none";
                      }}
                    >
                      {issue.camera_name}
                    </a>
                  ) : (
                    issue.camera_name || "N/A"
                  )}
                </TableCell>
                <TableCell sx={{ width: "20%" }}>
                  {formatPatrolTime(
                    typeof issue.timestamp === "string"
                      ? issue.timestamp
                      : String(issue.timestamp)
                  )}
                </TableCell>
                <TableCell sx={{ width: "30%" }}>{issue.description}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={3} align="center">
                No connectivity issues for this patrol
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
